import React, { useEffect, useState } from 'react';
import {
  createTipoJustificacion,
  CreateJustificacionTipoPayload,
  getTiposJustificacion,
  JustificacionTipo,
  removeTipoJustificacion,
  updateTipoJustificacion,
} from '../services/justificaciones.service';
import { getErrorMessage } from '../utils/errorHandler';

const duracionesDisponibles = [24, 48, 72, 96, 120, 144, 168];

const emptyForm: CreateJustificacionTipoPayload = {
  nombre: '',
  duracionHoras: 24,
  activo: true,
};

const TiposJustificacionesPage: React.FC = () => {
  const [tipos, setTipos] = useState<JustificacionTipo[]>([]);
  const [form, setForm] = useState<CreateJustificacionTipoPayload>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getTiposJustificacion();
      setTipos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editId) {
        await updateTipoJustificacion(editId, form);
        setSuccess('Tipo de justificacion actualizado');
      } else {
        await createTipoJustificacion(form);
        setSuccess('Tipo de justificacion creado');
      }
      resetForm();
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (tipo: JustificacionTipo) => {
    setEditId(tipo._id);
    setForm({
      nombre: tipo.nombre,
      duracionHoras: tipo.duracionHoras,
      activo: tipo.activo,
    });
  };

  const onDelete = async (id: string) => {
    setProcessingId(id);
    setError('');
    setSuccess('');
    try {
      await removeTipoJustificacion(id);
      setSuccess('Tipo de justificacion dado de baja');
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <>
      <h1 className="page-title">Novedades - Tipos</h1>

      <div className="card">
        <h2 className="card-title">Tipos de Novedad</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Duracion (horas)</label>
              <select
                value={form.duracionHoras}
                onChange={(e) => setForm((prev) => ({ ...prev, duracionHoras: Number(e.target.value) }))}
              >
                {duracionesDisponibles.map((duracion) => (
                  <option key={duracion} value={duracion}>
                    {duracion}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="checkbox-group">
            <input
              id="tipo-activo"
              type="checkbox"
              checked={Boolean(form.activo)}
              onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
            />
            <label htmlFor="tipo-activo">Activo</label>
          </div>

          <div className="actions-cell">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear tipo'}
            </button>
            {editId && (
              <button className="btn btn-outline" type="button" onClick={resetForm}>
                Cancelar edicion
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de tipos</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : tipos.length === 0 ? (
          <div className="empty-state">No hay tipos de justificacion registrados.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Duracion (h)</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tipos.map((tipo) => (
                  <tr key={tipo._id}>
                    <td>{tipo.nombre}</td>
                    <td>{tipo.duracionHoras}</td>
                    <td>{tipo.activo ? 'Si' : 'No'}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => onEdit(tipo)}>
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          type="button"
                          disabled={processingId === tipo._id}
                          onClick={() => onDelete(tipo._id)}
                        >
                          {processingId === tipo._id ? 'Procesando...' : 'Baja'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default TiposJustificacionesPage;
